import { isDefined } from "@clipboard-health/util-ts";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useInviteBoostSpendLimit } from "@src/appV2/redesign/SpendingControls/hooks/useInviteBoostSpendLimit";
import { ModalStatus, useModalState, type UseModalState } from "@src/lib/ui-react";
import { useCallback, useEffect } from "react";

import { majorToMinor, minorToMajor } from "../../../ShiftDetails/components/chargeRateUtils";
import {
  type InviteBlockReason,
  type InviteErrorReason,
  type InviteFlowStatus,
  type InviteWarningReason,
  type Professional,
  type ShiftDetails,
} from "../types";
import { useInviteFlow } from "./useInviteFlow";
import { useProfessionalSearch } from "./useProfessionalSearch";

/** Share of the workplace's hourly bonus that goes to the professional. */
const WORKER_BONUS_PERCENTAGE = 0.75;

interface UseInviteModeStateOptions {
  shiftDetails: ShiftDetails;
  /** When set, the professional is fixed and the search list is skipped (e.g. Chat, My Professionals). */
  preselectedProfessional?: Professional;
  onSuccess: () => void;
}

interface UseInviteModeStateResult {
  searchQuery: string;
  isSearching: boolean;
  allProfessionals: Professional[];
  starredProfessionals: Professional[];
  recentProfessionals: Professional[];
  setSearchQuery: (value: string) => void;

  status: InviteFlowStatus;
  selectedProfessional: Professional | undefined;
  postToMarketplace: boolean;
  blockReason: InviteBlockReason | undefined;
  canSendInvite: boolean;
  errorReasons: InviteErrorReason[];
  warningReasons: InviteWarningReason[];
  missingDocuments: string[] | undefined;
  errorDetail: string | undefined;
  startTimeError: string | undefined;
  errorModalState: UseModalState;
  blockedModalState: UseModalState;
  selectProfessional: (professional: Professional) => void;
  clearSelection: () => void;
  setPostToMarketplace: (value: boolean) => void;
  sendInvite: () => Promise<void>;
  checkIfBlocked: () => boolean;
  dismissError: () => void;

  postToMarketplaceInfoModalState: UseModalState;

  isChargeRateVisible: boolean;
  isChargeRateLoading: boolean;
  hourlyBonusInMinorUnits: number;
  maxBoostInMinorUnits: number | undefined;
  professionalBonusDisplay: string | undefined;
  onBoostChange: (boostInMinorUnits: number) => void;
  isBoostOverLimit: boolean;
  boostOverageInMinorUnits: number;
  isBoostIncreaseRequested: boolean;
  onRequestBoostIncrease: () => void;
  /** True when the boost exceeds (or is still checking against) the weekly spend limit; ANDed into `canSendInvite` by callers. */
  spendLimitBlocksSend: boolean;
  weekStartDate: Date;
  increaseRequest: ReturnType<typeof useInviteBoostSpendLimit>["increaseRequest"];
}

/**
 * Shared invite-composition state: professional search, `useInviteFlow`, and
 * hourly-boost pricing/spend-limit math.
 */
export function useInviteModeState(options: UseInviteModeStateOptions): UseInviteModeStateResult {
  const { shiftDetails, preselectedProfessional, onSuccess } = options;

  const postToMarketplaceInfoModalState = useModalState(ModalStatus.CLOSED);

  const hasPreselectedProfessional = isDefined(preselectedProfessional);

  const {
    searchQuery,
    isSearching,
    allProfessionals,
    starredProfessionals,
    recentProfessionals,
    setSearchQuery,
  } = useProfessionalSearch({
    workplaceId: shiftDetails.workplaceId,
    qualification: shiftDetails.qualification,
    skip: hasPreselectedProfessional,
  });

  const {
    status,
    selectedProfessional,
    postToMarketplace,
    blockReason,
    canSendInvite,
    errorReasons,
    warningReasons,
    missingDocuments,
    errorDetail,
    startTimeError,
    errorModalState,
    blockedModalState,
    baseChargeRate,
    chargeConstraints,
    customChargeRateBoost,
    isCustomRateBoostEnabled,
    isPricingLoading,
    setCustomChargeRateBoost,
    selectProfessional,
    clearSelection,
    setPostToMarketplace,
    sendInvite,
    checkIfBlocked,
    dismissError,
  } = useInviteFlow({ shiftDetails, preselectedProfessional, onSuccess });

  useEffect(() => {
    checkIfBlocked();
  }, [checkIfBlocked]);

  const maxBoostInMinorUnits =
    isDefined(baseChargeRate) && isDefined(chargeConstraints)
      ? chargeConstraints.max.amountInMinorUnits - baseChargeRate.amountInMinorUnits
      : undefined;

  const isChargeRateVisible = isCustomRateBoostEnabled && isDefined(selectedProfessional);

  const hourlyBonusInDollars = customChargeRateBoost ?? 0;
  const hourlyBonusInMinorUnits = majorToMinor(hourlyBonusInDollars);
  const professionalBonusInDollars =
    hourlyBonusInDollars > 0
      ? Math.floor(hourlyBonusInDollars * WORKER_BONUS_PERCENTAGE * 100) / 100
      : 0;

  const professionalBonusDisplay =
    isChargeRateVisible && isDefined(maxBoostInMinorUnits)
      ? `${professionalBonusInDollars > 0 ? "+" : ""}${formatDollarsAsUsd(
          professionalBonusInDollars
        )} /hr`
      : undefined;

  // No `editModalState` — the boost is edited inline, with no editor dialog to hide
  // behind the increase-request modal; that modal opens standalone here.
  const spendLimit = useInviteBoostSpendLimit({
    workplaceId: shiftDetails.workplaceId,
    qualification: shiftDetails.qualification,
    startTime: shiftDetails.startTime,
    endTime: shiftDetails.endTime,
    baseChargeRate,
    customChargeRateBoost,
    enabled: isChargeRateVisible,
  });

  const isCheckingSpendLimit =
    spendLimit.isActive && hourlyBonusInDollars > 0 && spendLimit.isLoading;
  const spendLimitBlocksSend = spendLimit.exceeded || isCheckingSpendLimit;

  const handleBoostChange = useCallback(
    (boostInMinorUnits: number) => {
      const boost = minorToMajor(boostInMinorUnits);
      setCustomChargeRateBoost(boost > 0 ? boost : undefined);
    },
    [setCustomChargeRateBoost]
  );

  const handleRequestBoostIncrease = useCallback(() => {
    spendLimit.increaseRequest.open({
      overageInMinorUnits: spendLimit.overageInMinorUnits,
      currentLimitInDollars: isDefined(spendLimit.limitInMinorUnits)
        ? minorToMajor(spendLimit.limitInMinorUnits)
        : undefined,
      qualification: shiftDetails.qualification,
    });
  }, [spendLimit, shiftDetails.qualification]);

  return {
    searchQuery,
    isSearching,
    allProfessionals,
    starredProfessionals,
    recentProfessionals,
    setSearchQuery,

    status,
    selectedProfessional,
    postToMarketplace,
    blockReason,
    canSendInvite,
    errorReasons,
    warningReasons,
    missingDocuments,
    errorDetail,
    startTimeError,
    errorModalState,
    blockedModalState,
    selectProfessional,
    clearSelection,
    setPostToMarketplace,
    sendInvite,
    checkIfBlocked,
    dismissError,

    postToMarketplaceInfoModalState,

    isChargeRateVisible,
    isChargeRateLoading: isCustomRateBoostEnabled && isPricingLoading,
    hourlyBonusInMinorUnits,
    maxBoostInMinorUnits,
    professionalBonusDisplay,
    onBoostChange: handleBoostChange,
    isBoostOverLimit: spendLimit.exceeded,
    boostOverageInMinorUnits: spendLimit.overageInMinorUnits,
    isBoostIncreaseRequested: spendLimit.increaseRequest.isRequested,
    onRequestBoostIncrease: handleRequestBoostIncrease,
    spendLimitBlocksSend,
    weekStartDate: spendLimit.weekStartDate,
    increaseRequest: spendLimit.increaseRequest,
  };
}
